/**
 * CRM enrichment runner (S9) — the profile enrichment CARD (machine-derived, provenance-tagged).
 *
 * Renders the enriched org/person record from the pilot-gated /enrichment/profile endpoint. Brad-only
 * pilot (renders nothing otherwise — and the endpoint is itself pilot-gated, so enrichment never reaches
 * the wider CRM team, B5). A teal ✦ tag marks the data as machine-derived, with the enrichment date +
 * model + confidence in the tooltip (services-spec §6.1). Renders nothing until an enrichment exists.
 */
import { useState } from 'react';

import {
  type EnrichFullRecord,
  type EnrichWebCandidate,
  type EnrichWebTrace,
  useEnrichmentAccess,
  useEnrichmentFull,
  useEnrichmentProfileData,
} from '../../hooks/useCrmEnrichment';
import styles from './crm.module.css';

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.fieldRow}>
      <span className={styles.fieldLabel}>{label}</span>
      <span className={styles.fieldValue}>{value}</span>
    </div>
  );
}

const _lbl: React.CSSProperties = { color: '#888', fontWeight: 600, fontSize: 11, paddingRight: 8 };
const _td: React.CSSProperties = { padding: '2px 8px', borderTop: '1px solid #EEE', verticalAlign: 'top' };

/** One raw Brave/Exa candidate — linked title + snippet, small/muted (a debugging aid, not a feature). */
function WebCandidateList({ label, candidates }: { label: string; candidates?: EnrichWebCandidate[] | null }) {
  if (!candidates || candidates.length === 0) return null;
  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ ..._lbl, marginBottom: 2 }}>{label}</div>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
        {candidates.map((c, i) => (
          <li key={c.url || i} style={{ marginBottom: 2 }}>
            {c.url
              ? <a href={c.url} target="_blank" rel="noreferrer">{c.title || c.url}</a>
              : (c.title || '(no title)')}
            {c.snippet && <span style={{ color: '#888' }}> — {c.snippet}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Testing-period web-search trace (E-5 diagnosis, brad 2026-07-24): the raw Brave/Exa candidates behind
 * the landed pick, so a bad result (wrong LinkedIn, missed employer bio page) can be diagnosed right on
 * the profile instead of re-guessing. Renders nothing once there's nothing to show — a no-op once
 * `crm_enrichment_trace_web_results_enabled` is turned off server-side.
 */
function WebTraceSection({ trace }: { trace?: EnrichWebTrace }) {
  const org = trace?.org;
  const person = trace?.person;
  const hasOrg = !!(org?.brave?.length || org?.exa?.length);
  const hasPerson = !!(person?.brave?.length || person?.exa?.length);
  if (!hasOrg && !hasPerson) return null;
  return (
    <div style={{ marginTop: 10, borderTop: '1px dashed #DDD', paddingTop: 8 }}>
      <div style={{ ..._lbl, marginBottom: 6 }}>WEB SEARCH TRACE (testing)</div>
      {hasOrg && (
        <>
          <WebCandidateList label="Org · Brave" candidates={org?.brave} />
          <WebCandidateList label="Org · Exa" candidates={org?.exa} />
        </>
      )}
      {hasPerson && (
        <>
          <WebCandidateList label="Person · Brave" candidates={person?.brave} />
          <WebCandidateList label="Person · Exa" candidates={person?.exa} />
        </>
      )}
    </div>
  );
}

/** The full structured record, rendered readably (not raw JSON) for the "See full enrichment" review. */
function FullEnrichment({ record, webTrace }: { record: EnrichFullRecord; webTrace?: EnrichWebTrace }) {
  const org = record.org || {};
  const firmo = (org.firmographics || {}) as Record<string, unknown>;
  const deep = org.deep_dive || {};
  const sig = org.signals || {};
  const traj = (sig.trajectory || {}) as Record<string, unknown>;
  const contacts = record.contacts || [];
  return (
    <div style={{ marginTop: 8, fontSize: 12 }}>
      {org.name && <Row label="Resolved name" value={String(org.name)} />}
      {Array.isArray(org.org_types) && org.org_types.length > 0 &&
        <Row label="Org types" value={org.org_types.join(', ')} />}
      {(org.type || org.stance) && <Row label="Disposition" value={`${org.type ?? '—'} · ${org.stance ?? '—'}`} />}
      {org.investor_type && <Row label="Investor type" value={String(org.investor_type)} />}
      {org.description && <Row label="About" value={String(org.description)} />}
      {Object.keys(firmo).length > 0 && (
        <Row label="Firmographics" value={Object.entries(firmo)
          .filter(([, v]) => v != null && v !== '').map(([k, v]) => `${k}: ${v}`).join(' · ')} />
      )}
      {org.salience != null && <Row label="Salience" value={`${org.salience}/10`} />}
      {deep.relationship_brief && <Row label="Relationship" value={String(deep.relationship_brief)} />}
      {Array.isArray(deep.next_steps) && deep.next_steps.length > 0 &&
        <Row label="Next steps" value={deep.next_steps.join(' · ')} />}
      {(traj.state || sig.meetings) && (
        <Row label="Signals" value={[
          traj.state ? `trajectory: ${traj.state}` : null,
          (sig.meetings as Record<string, unknown> | undefined)?.n_meetings != null
            ? `meetings: ${(sig.meetings as Record<string, unknown>).n_meetings}` : null,
        ].filter(Boolean).join(' · ')} />
      )}
      {contacts.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={{ ..._lbl, marginBottom: 2 }}>CONTACTS ({contacts.length})</div>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead><tr>
              {['name', 'title', 'seniority', 'function', 'role', 'LinkedIn'].map((h) => (
                <th key={h} style={{ ..._lbl, textAlign: 'left', padding: '2px 8px' }}>{h}</th>))}
            </tr></thead>
            <tbody>
              {contacts.map((c, i) => (
                <tr key={c.email || i}>
                  <td style={_td}>{c.name || c.email || '—'}</td>
                  <td style={_td}>{c.canonical_title || '—'}</td>
                  <td style={_td}>{c.seniority || '—'}</td>
                  <td style={_td}>{c.function || '—'}</td>
                  <td style={_td}>{c.role_to_ember || '—'}</td>
                  <td style={_td}>{c.linkedin
                    ? <a href={c.linkedin} target="_blank" rel="noreferrer">profile</a> : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <WebTraceSection trace={webTrace} />
    </div>
  );
}

export function CrmEnrichmentCard({ kind, id, embedded = false }: {
  kind: 'org' | 'person';
  id: number;
  /** Organization profiles fold these facts into the company-name panel instead of a second card. */
  embedded?: boolean;
}) {
  const isPilot = useEnrichmentAccess().data?.pilot === true;
  const q = useEnrichmentProfileData(kind, id, isPilot);
  const [expanded, setExpanded] = useState(false);
  const full = useEnrichmentFull(kind, id, isPilot && expanded);   // lazy — only fetches when opened
  if (!isPilot) return null;

  const e = q.data?.enrichment;
  if (!e) return null;   // no enrichment for this entity yet

  const firmo = (e.firmographics || {}) as Record<string, unknown>;
  const provTitle = [
    'machine-derived (AI enrichment)',
    e.model ? `model: ${e.model}` : null,
    e.confidence ? `confidence: ${e.confidence}` : null,
  ].filter(Boolean).join(' · ');

  return (
    <div className={embedded ? styles.enrichmentEmbedded : styles.card}>
      {!embedded && (
        <div className={styles.cardTitle}>
          Enrichment
          <span className={styles.enrichProv} title={provTitle}>
            ✦ {e.enriched_at ? `enriched ${e.enriched_at.slice(0, 10)}` : 'machine-derived'}
          </span>
        </div>
      )}
      {kind === 'org' ? (
        <>
          {e.investor_type && <Row label="Investor type" value={e.investor_type} />}
          {e.description && <Row label="About" value={e.description} />}
          {firmo.aum != null && <Row label="AUM" value={String(firmo.aum)} />}
          {firmo.hq != null && <Row label="HQ" value={String(firmo.hq)} />}
          {firmo.employees != null && <Row label="Employees" value={String(firmo.employees)} />}
          {firmo.founded != null && <Row label="Founded" value={String(firmo.founded)} />}
          {e.salience != null && <Row label="Salience" value={`${e.salience}/10`} />}
        </>
      ) : (
        <>
          {e.role && <Row label="Role to Ember" value={e.role} />}
          {e.function && <Row label="Function" value={e.function} />}
        </>
      )}

      <button
        onClick={() => setExpanded((v) => !v)}
        style={{ marginTop: 10, font: 'inherit', fontSize: 12, background: 'none', border: 'none',
                 color: '#2563EB', cursor: 'pointer', padding: 0 }}>
        {expanded ? '▾ Hide full enrichment' : '▸ See full enrichment'}
      </button>
      {expanded && (
        full.isLoading ? <div style={{ color: '#888', fontSize: 12, marginTop: 6 }}>Loading…</div>
          : full.isError ? <div style={{ color: '#C0392B', fontSize: 12, marginTop: 6 }}>
              Couldn&apos;t load the full record.
            </div>
          : full.data?.record
            ? <FullEnrichment record={full.data.record} webTrace={full.data.web_trace} />
            : <div style={{ color: '#888', fontSize: 12, marginTop: 6 }}>
                No detailed record stored for this entity yet (runs after this change persist it).
              </div>
      )}
    </div>
  );
}
